import React, { useState, useMemo } from "react";
import Layout from "../layout/Layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileAlt,
  faDownload,
  faSpinner,
  faSearch,
  faFileCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { procesos } from "../utils/procesos";

const tipoIconos = {
  Word: faFileWord,
  Excel: faFileExcel,
  PDF: faFilePdf,
};

const tipoColores = {
  Word: { bg: "bg-blue-50", icon: "text-blue-600", badge: "bg-blue-600", border: "border-blue-200", hover: "hover:bg-blue-100" },
  Excel: { bg: "bg-green-50", icon: "text-green-600", badge: "bg-green-600", border: "border-green-200", hover: "hover:bg-green-100" },
  PDF: { bg: "bg-red-50", icon: "text-red-600", badge: "bg-red-600", border: "border-red-200", hover: "hover:bg-red-100" },
};

const FormatoCard = ({ doc, onDownload, descargando, procesoOrigen }) => {
  const tc = tipoColores[doc.tipo] || tipoColores.Word;
  const Icon = tipoIconos[doc.tipo] || faFileAlt;
  const isDownloading = descargando === doc.archivo;

  return (
    <div
      className={`${tc.bg} ${tc.border} border rounded-xl shadow-sm p-6 flex flex-col hover:shadow-lg hover:${tc.hover} transition-all duration-300 hover:-translate-y-1`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`${tc.icon} text-4xl`}>
          <FontAwesomeIcon icon={Icon} />
        </div>
        <span className={`${tc.badge} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
          {doc.tipo}
        </span>
      </div>
      <h3 className="font-bold text-gray-800 text-sm leading-snug mb-1 line-clamp-3 min-h-[3rem]" title={doc.titulo}>
        {doc.titulo}
      </h3>
      <p className="text-gray-500 text-xs mb-4 flex-grow">{doc.descripcion}</p>
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
        <FontAwesomeIcon icon={faFileAlt} className="text-[10px]" />
        <span>{procesoOrigen}</span>
      </div>
      <button
        className={`w-full ${tc.badge} text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${isDownloading ? "animate-pulse" : ""}`}
        onClick={() => onDownload(doc.archivo)}
        disabled={isDownloading}
        aria-label={`Descargar ${doc.titulo}`}
      >
        {isDownloading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Descargando...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faDownload} />
            Descargar
          </>
        )}
      </button>
    </div>
  );
};

const Formatos = () => {
  const [procesoSeleccionado, setProcesoSeleccionado] = useState("todos");
  const [descargando, setDescargando] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  const proceso = procesos.find((p) => p.id === procesoSeleccionado);

  const documentosFiltrados = useMemo(() => {
    let resultado = busqueda
      ? procesos.flatMap((p) =>
          p.documentos
            .filter((doc) =>
              doc.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
              doc.descripcion.toLowerCase().includes(busqueda.toLowerCase()) ||
              doc.tipoFormato.toLowerCase().includes(busqueda.toLowerCase()) ||
              doc.tipo.toLowerCase().includes(busqueda.toLowerCase())
            )
            .map((doc) => ({ ...doc, procesoOrigen: p.nombre }))
        )
      : procesos.flatMap((p) =>
          p.documentos.map((doc) => ({ ...doc, procesoOrigen: p.nombre }))
        );

    if (procesoSeleccionado && procesoSeleccionado !== "todos") {
      resultado = resultado.filter((doc) => doc.procesoOrigen === proceso.nombre);
    }

    return resultado;
  }, [busqueda, procesoSeleccionado, proceso]);

  const handleDownload = (archivo) => {
    setDescargando(archivo);
    try {
      const link = document.createElement("a");
      link.href = `./docs/formatos/${archivo}`;
      link.download = archivo;
      link.click();
      setTimeout(() => {
        setDescargando(null);
      }, 2000);
    } catch (error) {
      setDescargando(null);
    }
  };

  return (
    <Layout>
      <div className="relative overflow-hidden">
        <div className="h-[320px] w-full relative">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/img/formatos-hero.jpg')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/20 to-white/10" />
          <div className="relative h-full flex items-center px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-white/10 backdrop-blur-sm rounded-full p-3">
                  <FontAwesomeIcon icon={faFileAlt} className="text-white text-2xl" />
                </div>
                <h1 className="text-4xl font-black text-white">Formatos</h1>
              </div>
              <p className="text-gray-300 text-lg max-w-xl">
                Descarga los formatos oficiales para trámites y servicios del Cuerpo de Bomberos de Manta.
              </p>
            </div>
          </div>
        </div>

        <div className="w-full flex justify-center -mt-8 relative z-10 mb-2">
          <div className="w-11/12 max-w-5xl bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar formato por nombre, tipo o descripción..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all"
                  aria-label="Buscar formatos"
                />
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                {documentosFiltrados.length} documento{documentosFiltrados.length !== 1 ? "s" : ""}
              </div>
            </div>

            <div className="flex gap-2 mb-6">
              <button
                onClick={() => {
                  setProcesoSeleccionado("todos");
                  setBusqueda("");
                }}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  procesoSeleccionado === "todos"
                    ? "bg-gray-800 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Todos
              </button>
              {procesos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setProcesoSeleccionado(p.id);
                    setBusqueda("");
                  }}
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    procesoSeleccionado === p.id
                      ? "bg-gray-800 text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {p.nombre}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-8 pb-[80pt]">
        <div className="max-w-6xl mx-auto mt-2">
          {documentosFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-2xl">
              <div className="bg-gray-200 rounded-full p-4 mb-4">
                <FontAwesomeIcon icon={faFileCircleQuestion} className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-600 mb-2">No se encontraron formatos</h3>
              <p className="text-gray-400 text-sm text-center max-w-md">
                No encontramos resultados con "{busqueda}". Intenta con otro término de búsqueda.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documentosFiltrados.map((doc) => (
                <FormatoCard
                  key={doc.id}
                  doc={doc}
                  onDownload={handleDownload}
                  descargando={descargando}
                  procesoOrigen={doc.procesoOrigen}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Formatos;
